import React from 'react';

/**
 * StatCard / StatGroup — zentrale, responsive Statistik-/KPI-Karten (Issue #10).
 *
 * Verbindliches Mobil-Muster (Referenz: /dog-management-beta):
 *  - < sm: kleines Icon (h-4 w-4) direkt VOR dem Label, KEINE große Icon-Kachel;
 *          Kennzahl darunter (text-2xl, tabular-nums), Zusatztext darunter.
 *  - >= sm: 40-px-Icon-Kachel (h-10 w-10, rounded-lg, getönter Hintergrund),
 *           Icon h-5 w-5, Kennzahl text-3xl. Desktop bleibt unverändert.
 *
 * Statusfarben nur semantisch über `tone` (Icon) bzw. `valueTone` (Kennzahl).
 * Neutrale Kennzahlen bleiben in normaler Textfarbe.
 *
 * Tailwind-Hinweis: Farbklassen sind bewusst als vollständige, statische
 * Strings hinterlegt (keine dynamische Interpolation), damit der JIT/Purge
 * sie behält.
 */

export type StatTone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

type StatIcon = React.ComponentType<{
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
}>;

const ICON_TEXT: Record<StatTone, string> = {
  primary: 'text-primary-600 dark:text-primary-400',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  error: 'text-error-600 dark:text-error-400',
  info: 'text-info-600 dark:text-info-400',
  neutral: 'text-gray-500 dark:text-gray-400',
};

// Getönte Kachel nur ab sm (mobil transparent).
const TILE_BG: Record<StatTone, string> = {
  primary: 'sm:bg-primary-50 sm:dark:bg-primary-900/20',
  success: 'sm:bg-success-50 sm:dark:bg-success-900/20',
  warning: 'sm:bg-warning-50 sm:dark:bg-warning-900/20',
  error: 'sm:bg-error-50 sm:dark:bg-error-900/20',
  info: 'sm:bg-info-50 sm:dark:bg-info-900/20',
  neutral: 'sm:bg-gray-100 sm:dark:bg-gray-700/40',
};

const VALUE_TEXT: Record<StatTone, string> = {
  primary: 'text-primary-600 dark:text-primary-400',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  error: 'text-error-600 dark:text-error-400',
  info: 'text-info-600 dark:text-info-400',
  neutral: 'text-gray-900 dark:text-white',
};

export interface StatCardProps {
  /** Semantisches Icon (Heroicon o.ä.). */
  icon: StatIcon;
  /** Label; wird vollständig ausgeschrieben, darf auf 2 Zeilen umbrechen. */
  label: string;
  /** Kennzahl (Zahl, Betrag, ReactNode). */
  value: React.ReactNode;
  /** Optionaler Zusatztext unter der Kennzahl. */
  sub?: React.ReactNode;
  /** Farbton für Icon + Kachel. Standard: primary. */
  tone?: StatTone;
  /** Farbton der Kennzahl. Standard: neutral (normale Textfarbe). */
  valueTone?: StatTone;
  /** Desktop-Trennlinie links (wird von StatGroup automatisch gesetzt). */
  divider?: boolean;
  /** Zusätzliche Wrapper-Klassen. */
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  icon: Icon,
  label,
  value,
  sub,
  tone = 'primary',
  valueTone = 'neutral',
  divider = false,
  className = '',
}) => (
  <div
    className={`app-stat-card min-w-0 overflow-hidden sm:flex sm:items-center sm:gap-3 ${
      divider ? 'sm:border-l sm:border-gray-200 sm:pl-6 sm:dark:border-gray-700' : ''
    } ${className}`.trim()}
  >
    <div
      className={`hidden flex-shrink-0 items-center justify-center bg-transparent sm:flex sm:h-10 sm:w-10 sm:rounded-lg ${TILE_BG[tone]}`}
    >
      <Icon className={`h-5 w-5 ${ICON_TEXT[tone]}`} aria-hidden="true" />
    </div>
    <div className="min-w-0 flex-1">
      {/*
        Mobil zwei Zeilen Höhe reservieren (max. 2 Zeilen laut Issue #10), damit
        die Kennzahlen benachbarter Karten auf einer Linie sitzen — auch wenn ein
        Label umbricht und ein anderes nicht.
      */}
      <div className="mb-1 flex min-h-[1.6em] min-w-0 items-start gap-1.5 sm:mb-0.5 sm:min-h-0">
        <Icon className={`mt-px h-3.5 w-3.5 flex-shrink-0 sm:hidden ${ICON_TEXT[tone]}`} aria-hidden="true" />
        <p className="line-clamp-2 min-w-0 break-words text-[10px] font-medium uppercase leading-tight tracking-wider text-gray-500 dark:text-gray-400">
          {label}
        </p>
      </div>
      <p className={`app-stat-value max-w-full text-[clamp(1.125rem,6vw,1.5rem)] font-bold leading-tight tabular-nums sm:text-3xl ${VALUE_TEXT[valueTone]}`}>
        {value}
      </p>
      {sub != null && sub !== '' && (
        <div className="mt-0.5 min-w-0 break-words text-xs leading-snug text-gray-500 dark:text-gray-400 [&>span]:max-w-full [&>span]:flex-wrap">
          {sub}
        </div>
      )}
    </div>
  </div>
);

const MOBILE_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

const SM_COLS: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  5: 'sm:grid-cols-5',
  6: 'sm:grid-cols-6',
};

export interface StatGroupProps {
  /** StatCard-Kinder. */
  children: React.ReactNode;
  /** Spaltenzahl ab sm (Desktop). Standard: an Kinderzahl orientiert. */
  columns?: number;
  /** Spaltenzahl mobil (unter sm). Standard: bis 3 Kennzahlen eine Zeile, sonst 2. */
  mobileColumns?: number;
  /** Zusätzliche Klassen für die umschließende Fläche. */
  className?: string;
}

/**
 * Zusammengehörige Statistikfläche mit responsivem Raster und automatischen
 * Desktop-Trennlinien. Mobil: bei bis zu 3 Kennzahlen eine Zeile, ab 4 ein
 * kompaktes 2-Spalten-Raster (kein Überlauf bei 320 px).
 */
export const StatGroup: React.FC<StatGroupProps> = ({ children, columns, mobileColumns, className = '' }) => {
  const items = React.Children.toArray(children).filter(React.isValidElement);
  const count = items.length;
  const smCount = columns ?? count;
  const requestedMobileCount = mobileColumns ?? count;
  // Auf echten Phone-Breiten sind drei oder vier KPI-Spalten zu schmal für
  // Beträge und längere deutsche Labels. Einspaltige Gruppen bleiben erhalten,
  // alle anderen werden bis zum sm-Breakpoint auf höchstens zwei Spalten begrenzt.
  const mobileCount = Math.min(requestedMobileCount, 2);
  const mobile = MOBILE_COLS[mobileCount] || 'grid-cols-2';
  const desktop = SM_COLS[smCount] || 'sm:grid-cols-3';

  const withDividers = items.map((child, i) =>
    React.cloneElement(child as React.ReactElement<StatCardProps>, {
      divider: i > 0 && (child.props as StatCardProps).divider !== false ? true : (child.props as StatCardProps).divider,
    }),
  );

  return (
    <div
      className={`app-stat-group rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5 ${className}`.trim()}
    >
      <div className={`grid ${mobile} ${desktop} items-start gap-2 sm:items-center sm:gap-6`}>
        {withDividers}
      </div>
    </div>
  );
};

export default StatCard;
